import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiXCircle } from 'react-icons/fi';
import { EmptyState } from '../dashboard/states';
import Badge from './Badge';
import SmsOutreachService from '../../services/SmsOutreachService';
import { fmtDateTime, fmtNumber, relativeTime } from './format';

const STATUSES = [
  { id: 'scheduled', label: 'Queued' },
  { id: 'sent', label: 'Sent' },
  { id: 'sent_dry_run', label: 'Sent (dry)' },
  { id: 'skipped', label: 'Skipped' },
  { id: 'failed', label: 'Failed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'all', label: 'All' },
];
const TOUCHES = [
  { id: 'all', label: 'All touches' },
  { id: 'pre_arrival', label: 'First text' },
  { id: 'reminder', label: 'Reminder' },
];

const STATUS_TONE = {
  scheduled: 'indigo',
  sending: 'amber',
  sent: 'emerald',
  sent_dry_run: 'sky',
  failed: 'rose',
  skipped: 'zinc',
  cancelled: 'zinc',
};
const STATUS_LABEL = { scheduled: 'queued', sending: 'sending', sent: 'sent', sent_dry_run: 'sent (dry)', failed: 'failed', skipped: 'skipped', cancelled: 'cancelled' };
const TOUCH_LABEL = { pre_arrival: 'first text', reminder: 'reminder' };
const SKIP_LABEL = {
  already_ordered: 'guest already ordered',
  already_texted: 'already texted',
  duplicate_in_run: 'duplicate',
  invalid_phone: 'invalid phone',
  stay_changed: 'stay dates changed',
  manual: 'cancelled by admin',
};

const PAGE_SIZE = 25;

function fmtLocal(value, tz) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz || 'America/Los_Angeles' });
  } catch (_) {
    return fmtDateTime(value);
  }
}

/**
 * Planned texts (messaging.scheduled_messages): what the dispatcher will send
 * and when, per guest and touch. Full admins can cancel a queued text.
 */
export default function UpcomingTable({ canOperate, onCancelled, refreshKey = 0 }) {
  const [status, setStatus] = useState('scheduled');
  const [touch, setTouch] = useState('all');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, totalPages: 1, byStatus: {}, queue: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => setPage(1), [status, touch, debouncedQ]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError('');
    try {
      const res = await SmsOutreachService.upcoming({ status, touch, q: debouncedQ || undefined, page, limit: PAGE_SIZE });
      if (id === reqId.current) setData(res || { items: [], total: 0, totalPages: 1, byStatus: {}, queue: [] });
    } catch (e) {
      if (id === reqId.current) setError(e.response?.data?.error || e.message || 'Failed to load upcoming texts');
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [status, touch, debouncedQ, page]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const cancel = async (id) => {
    setCancelling(id);
    try {
      await SmsOutreachService.cancelScheduled(id, 'manual');
      setConfirmId(null);
      onCancelled?.();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not cancel');
    } finally {
      setCancelling(null);
    }
  };

  const rows = useMemo(() => data.items || [], [data]);
  const byStatus = data.byStatus || {};
  const queued = (byStatus.scheduled || 0) + (byStatus.sending || 0);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
          {STATUSES.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setStatus(o.id)}
              className={`h-7 rounded-md px-2.5 text-xs font-medium transition-colors ${status === o.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
            >
              {o.label}
              {o.id === 'scheduled' && queued ? <span className="ml-1 text-[10px] text-indigo-600">{fmtNumber(queued)}</span> : null}
            </button>
          ))}
        </div>
        <div className="flex items-center rounded-lg bg-zinc-100 p-0.5">
          {TOUCHES.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setTouch(o.id)}
              className={`h-7 rounded-md px-2.5 text-xs font-medium transition-colors ${touch === o.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <label className="relative ml-auto">
          <FiSearch className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Phone (last digits) or CID"
            className="h-7 w-56 rounded-lg border border-zinc-200 bg-white pl-7 pr-2 text-xs outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
          />
        </label>
      </div>

      {data.queue?.length ? (
        <div className="mb-2 flex flex-wrap gap-1.5 text-[10.5px] text-zinc-600">
          {data.queue.map((r) => (
            <span key={`${r.touch}-${r.arm}`} className="rounded-md bg-zinc-50 px-2 py-0.5 ring-1 ring-zinc-200">
              <span className="font-semibold text-zinc-800">{fmtNumber(r.count)}</span> {TOUCH_LABEL[r.touch] || r.touch} · {r.arm}
              {r.overdue ? <span className="ml-1 text-amber-700">({fmtNumber(r.overdue)} due now)</span> : null}
            </span>
          ))}
          {data.nextAt ? (
            <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-indigo-700 ring-1 ring-indigo-100">
              next send {fmtDateTime(data.nextAt)} PT · {relativeTime(data.nextAt)}
            </span>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="mb-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</p> : null}

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-zinc-200">
        {loading && !rows.length ? (
          <div className="h-full w-full animate-pulse bg-zinc-50" />
        ) : rows.length === 0 ? (
          <EmptyState
            title={status === 'scheduled' ? 'Nothing queued' : 'No texts match'}
            description={status === 'scheduled' ? 'The planner runs every morning at 4 AM Pacific and fills this queue for the next week of arrivals.' : 'Try another status or clear the search.'}
          />
        ) : (
          <table className="min-w-full text-left text-xs">
            <thead className="sticky top-0 z-10 bg-zinc-50 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2">Sends at (guest local)</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Touch</th>
                <th className="px-3 py-2">Arm</th>
                <th className="px-3 py-2">Guest</th>
                <th className="px-3 py-2">CID</th>
                <th className="px-3 py-2">Stay</th>
                <th className="px-3 py-2">Lead</th>
                <th className="px-3 py-2">Detail</th>
                {canOperate ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 bg-white">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="font-medium text-zinc-900">{fmtLocal(r.scheduledFor, r.tz)}</div>
                    <div className="text-[10px] text-zinc-400">
                      {r.tz || '—'}
                      {r.tzSource && r.tzSource !== 'phone' ? ` (${r.tzSource})` : ''} · {fmtDateTime(r.scheduledFor)} PT · {relativeTime(r.scheduledFor)}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={STATUS_TONE[r.status] || 'zinc'} pulse={r.status === 'sending'}>
                      {STATUS_LABEL[r.status] || r.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-zinc-700">
                    {TOUCH_LABEL[r.touch] || r.touch}
                    {r.late ? <span className="ml-1 text-[10px] text-amber-600">late booking</span> : null}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={r.arm === 'treatment' ? 'indigo' : 'zinc'}>{r.arm || 'default'}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-800">{r.firstName || '—'}</div>
                    <div className="font-mono text-[10px] text-zinc-400">{r.phone || '—'}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-700">{r.confirmationId || '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-zinc-700">
                    {r.checkInDate || '—'}
                    {r.checkOutDate ? <span className="text-zinc-400"> → {r.checkOutDate}</span> : null}
                    {r.stayNights != null ? <span className="block text-[10px] text-zinc-400">{r.stayNights} nights · {r.locality || ''}</span> : null}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-zinc-600">{r.leadDays != null ? `d+${r.leadDays}` : '—'}</td>
                  <td className="max-w-[260px] truncate px-3 py-2 text-zinc-500" title={r.lastError || r.skipReason || ''}>
                    {r.status === 'skipped' || r.status === 'cancelled' ? SKIP_LABEL[r.skipReason] || r.skipReason || '—' : null}
                    {r.status === 'failed' ? r.lastError || 'failed' : null}
                    {r.status === 'sent' || r.status === 'sent_dry_run' ? `${fmtDateTime(r.sentAt)} PT · ${r.templateKey}` : null}
                    {r.status === 'scheduled' || r.status === 'sending' ? `template ${r.templateKey}${r.skipIfOrdered ? ' · skipped if guest orders first' : ''}` : null}
                  </td>
                  {canOperate ? (
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {r.status === 'scheduled' ? (
                        confirmId === r.id ? (
                          <span className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              disabled={cancelling === r.id}
                              onClick={() => cancel(r.id)}
                              className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                            >
                              {cancelling === r.id ? 'Cancelling…' : 'Confirm cancel'}
                            </button>
                            <button type="button" onClick={() => setConfirmId(null)} className="rounded-md px-2 py-1 text-[11px] text-zinc-500 hover:text-zinc-800">
                              Keep
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmId(r.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-1 text-[11px] font-medium text-zinc-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                            aria-label={`Cancel text to ${r.phone || r.firstName || 'guest'}`}
                          >
                            <FiXCircle className="h-3 w-3" /> Cancel
                          </button>
                        )
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
        <span>
          {fmtNumber(data.total)} {status === 'scheduled' ? 'queued' : 'matching'} text{data.total === 1 ? '' : 's'}
          {byStatus.sent != null ? ` · ${fmtNumber(byStatus.sent)} sent · ${fmtNumber(byStatus.sent_dry_run || 0)} dry · ${fmtNumber(byStatus.skipped || 0)} skipped · ${fmtNumber(byStatus.cancelled || 0)} cancelled` : ''}
        </span>
        <span className="flex items-center gap-2">
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-zinc-200 px-2 py-1 disabled:opacity-40">
            Prev
          </button>
          <span className="tabular-nums">
            {page} / {data.totalPages || 1}
          </span>
          <button
            type="button"
            disabled={page >= (data.totalPages || 1) || loading}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border border-zinc-200 px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </span>
      </div>
    </div>
  );
}
